import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, branch, renderComponent, withHandlers } from 'recompose';

import { withSparefootSelfStorageSchema } from 'decorators';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import { Tooltip } from 'components/core/Tooltip';
import Marker from 'components/svg/Marker';
import OnlineMoveIn from 'components/svg/OnlineMoveIn';
import { FlexColumn, FlexRow } from 'components/atoms/Flex';
import { FacilityImage } from 'components/facility/FacilityImage';
import ImageNotAvailable from 'components/facility/ImageNotAvailable/ImageNotAvailable';
import imageComingSoon from 'assets/images/facility/image-coming-soon.png';
import { UnitList, LinkedUnitDetails } from 'components/molecules/UnitList';
import { Badge } from 'components/molecules/Badge';
import StarRating from 'components/molecules/StarRating/StarRating';
import { trackCTAClick } from 'components/core/library/tracking-utils/tracking/clickTrackingUtils';
import { GhostFacilityCard } from 'components/search/GhostFacilityCard';

import trustedPatnerBadge from 'assets/images/icons/trusted-partner-badge.svg';
import questionMarkIcon from 'assets/images/icons/icon-question-mark.svg';
import './FacilityCard.scss';

export const enhance = compose(
	withHandlers({
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				if (handleFacilityDeselected) {
					handleFacilityDeselected();
				}
				if (handlePinDeselected) {
					handlePinDeselected();
				}
			},
		handleSelect:
			({ handleFacilitySelected, handlePinDeselected, facility }) =>
			() => {
				if (handleFacilitySelected && facility?.id) {
					handleFacilitySelected(facility.id);
				}
				if (handlePinDeselected) {
					handlePinDeselected();
				}
			},
		handleTrackCTA:
			({ facility, baseUrl }) =>
			() => {
				if (facility) {
					const facilityUrl = facility?.url?.facility
						? `${baseUrl}${facility.url.facility}`
						: undefined;
					trackCTAClick(
						'view-all-units',
						'View All units Button',
						'View All units',
						facilityUrl,
					);
				}
			},
	}),
	withSparefootSelfStorageSchema,
	branch(({ loading }) => loading, renderComponent(GhostFacilityCard)),
);

export function FacilityCard({
	facility,
	baseUrl,
	facilitySelected,
	handleDeselect,
	handleSelect,
	handleTrackCTA,
	lazyLoad,
	hasOMI,
	selfStorageSchema,
	withSchema,
	isHighlighted,
	isVipFacility,
	tooltipMessageForVIPFacility,
	searchParams,
	storageType,
}) {
	return (
		<FlexColumn
			className={classnames(
				'new-clp-facility-card',
				'facility-card-loaded',
				{
					selected: facility?.id === facilitySelected,
					highlighted: isHighlighted,
					'vip-facility': isVipFacility,
				},
			)}
			onMouseEnter={handleSelect}
			onMouseLeave={handleDeselect}
			itemScope
			itemType="https://schema.org/LocalBusiness"
		>
			<FlexColumn
				gapSmall
				className="facility-head"
			>
				<Link
					href={`${baseUrl}${facility?.url?.facility ?? ''}`}
					hideBorder
					productClick
				>
					{facility?.images?.[0] ? (
						<FacilityImage
							itemProp="url"
							src={facility.images[0]}
							alt={`Photo of ${
								facility?.name ?? 'storage facility'
							}`}
							size="medium"
							lazyLoad={lazyLoad}
						/>
					) : (
						<ImageNotAvailable
							size="medium"
							defaultImage={imageComingSoon}
							imageText={null}
						/>
					)}
				</Link>
				{isVipFacility && (
					<div className="vip-facility__badge">
						<div className="vip-facility__badge-content">
							<Image
								className="trusted-partner-badge-img"
								src={trustedPatnerBadge}
								alt="Trusted Partner Badge"
							/>
							<div className="vip-facility__badge-text">
								<div className="vip-facility__badge-top-text">
									Trusted Partner Facility
								</div>
								<div className="vip-facility__badge-bottom-text">
									Top-rated facility with exceptional service
								</div>
							</div>
							{!!tooltipMessageForVIPFacility && (
								<Tooltip
									content={tooltipMessageForVIPFacility}
									iconRightSide
									tooltipShowing
									onFocus
									clickOnMobile
									plainText
								>
									<Image
										className="question-mark-icon-img"
										src={questionMarkIcon}
										alt="Question Mark Icon"
									/>
								</Tooltip>
							)}
						</div>
					</div>
				)}
			</FlexColumn>
			<FlexColumn
				className="facility-details"
				gapSmall
			>
				<FlexRow
					justifyBetween
					alignCenter
					gapSmall
				>
					<Badge
						badgeColor="lightestGray"
						badgeIcon={<Marker fill="#ff336a" />}
						badgeText={
							<Paragraph
								size="tiny"
								color="black"
								weight="standard"
								last
							>
								<Text
									size="tiny"
									color="black"
									weight="extraBold"
								>
									{`${facility?.distance ?? ''}`}
								</Text>{' '}
								miles away
							</Paragraph>
						}
					/>
					{!!facility?.reviewMetadata?.count && (
						<Badge
							badgeColor="lightestGray"
							badgeText={
								<FlexRow
									alignCenter
									gapTiny
								>
									<StarRating
										width={9}
										height={9}
										rating={facility.reviewMetadata.average}
										fillColor="#146FB4"
										outOf={5}
									/>
									<Text
										size="tiny"
										color="black"
										weight="extraBold"
										last
									>
										{facility.reviewMetadata.average?.toFixed(
											1,
										)}
									</Text>
									<Text
										size="tiny"
										weight="light"
										color="black"
										last
									>
										({facility.reviewMetadata.count})
									</Text>
								</FlexRow>
							}
						/>
					)}
				</FlexRow>
				<Link
					href={`${baseUrl}${facility?.url?.facility ?? ''}`}
					hideBorder
				>
					<FlexColumn gapSmall>
						<H3
							className="facility-name"
							color="black"
							itemProp="name"
							last
						>
							{facility?.name}
						</H3>
						<Paragraph
							className="facility-location"
							itemProp="address"
							itemScope
							last
							itemType="https://schema.org/PostalAddress"
							color="gray"
						>
							{[
								facility?.location?.streetAddress,
								facility?.location?.city,
								facility?.location?.state,
								facility?.location?.zip,
							]
								.filter(Boolean)
								.join(', ')}
						</Paragraph>
					</FlexColumn>
				</Link>
				<UnitList
					facility={facility}
					unitDetailsComponent={LinkedUnitDetails}
					units={facility.units}
					searchParams={searchParams}
					searchType={storageType}
				/>
				{hasOMI && (
					<Badge
						className="facility-card-omi-badge"
						badgeColor="green"
						badgeIcon={<OnlineMoveIn />}
						badgeText={
							<Text
								size="tiny"
								weight="extraBold"
								last
							>
								Online Move-In Available
							</Text>
						}
					/>
				)}
				<Button
					className="view-all-units ph-button button-primary button-pink"
					href={`${baseUrl}${facility?.url.facility ?? ''}`}
					onClick={handleTrackCTA}
				>
					<Text
						strong
						color="white"
						weight="bold"
					>
						View all units
					</Text>
				</Button>
			</FlexColumn>
			{withSchema && selfStorageSchema && (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</FlexColumn>
	);
}

FacilityCard.propTypes = {
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleSelect: PropTypes.func,
	lazyLoad: PropTypes.bool,
	handleTrackCTA: PropTypes.func,
	handleDeselect: PropTypes.func,
	searchParams: PropTypes.object,
	selfStorageSchema: PropTypes.string,
	storageType: PropTypes.oneOf(['storage', 'vehicle']),
	withSchema: PropTypes.bool,
	hasOMI: PropTypes.bool,
	onVisible: PropTypes.func,
	percentVisible: PropTypes.number,
	isHighlighted: PropTypes.bool,
	isVipFacility: PropTypes.bool,
	tooltipMessageForVIPFacility: PropTypes.string,
};

FacilityCard.defaultProps = {
	baseUrl: '',
	storageType: 'storage',
	lazyLoad: true,
	tooltipMessageForVIPFacility:
		'Trusted Partners maintain facilities with at least four-star ratings.',
};

export default enhance(FacilityCard);
